import {
  IOverTargetLine,
  IStationOverTarget,
  IStationOverTargetResult,
} from "../../../models/ProductionDashboard";

/**
 * One colour per dismantling line, assigned in the order the API lists the lines (production
 * lines first, by name) and never re-assigned when lines are hidden - a line keeps its colour
 * whether or not its neighbour is shown. Eight distinct hues is the ceiling for telling series
 * apart; a ninth line folds into "Other lines" rather than getting a colour nobody can tell from
 * the others.
 */
export const LINE_COLOURS = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
];

/** Vehicles that were on no bay or line when the station ran, and the folded "Other lines". */
export const NEUTRAL_LINE_COLOUR = "#8a8a85";

export const OTHER_LINES_KEY = "other";
export const OTHER_LINES_LABEL = "Other lines";
export const NO_LINE_KEY = "none";

export interface IOverTargetSeries {
  /** Bay id, "none" for the no-line bucket, or "other" for the folded extras. */
  key: string;
  label: string;
  colour: string;
  /** Vehicles that went over target (by any amount) per station - the plotted figure, same order as the chart's stations. */
  overCounts: number[];
  /** Vehicles that went through per station, for the tooltip. */
  vehicleCounts: number[];
  /** Seconds past target summed over the vehicles that went over, for the tooltip's total and average. */
  overSeconds: number[];
}

export interface IOverTargetChart {
  /** Stations in flow order, after any zero-filtering. */
  stations: IStationOverTarget[];
  labels: string[];
  series: IOverTargetSeries[];
  /** Names of stations with no target set: they can never show time over, and the user should know why. */
  untargeted: string[];
  /** True when every plotted value is zero - the chart would be an empty frame. */
  isEmpty: boolean;
}

export interface IOverTargetChartOptions {
  /** Drop stations where no vehicle on any line went over target. */
  hideZeroStations?: boolean;
  /** Series keys to plot; undefined plots every line. Hidden lines keep their colour. */
  visibleKeys?: string[];
}

/**
 * Minutes past target per vehicle that went over. Zero over-vehicles is zero, not NaN - the
 * station simply had nothing to average.
 */
export function averageOverMinutes(overSeconds: number, overCount: number): number {
  return overCount > 0 ? toMinutes(overSeconds / overCount) : 0;
}

export function lineKey(line: IOverTargetLine): string {
  return line.dismantlingBayId ?? NO_LINE_KEY;
}

/** The colour a line is drawn in, by its position in the API's list. */
export function lineColour(line: IOverTargetLine, index: number): string {
  if (line.dismantlingBayId === null) return NEUTRAL_LINE_COLOUR;
  return LINE_COLOURS[index] ?? NEUTRAL_LINE_COLOUR;
}

/** Seconds as minutes to one decimal place, so a 30-second overrun is not rounded away. */
export function toMinutes(seconds: number): number {
  return Math.round((seconds / 60) * 10) / 10;
}

/**
 * The series and labels for the stacked bar. Each line is a series in the API's order, with
 * named bays and lines taking the first eight colours and anything past that folded into one
 * neutral "Other lines" series. Stations run along the x-axis in flow order; the bar height is
 * the number of vehicles that went over, which stacks across lines because each vehicle is on
 * exactly one line.
 */
export function buildOverTargetChart(
  result: IStationOverTargetResult | undefined,
  options: IOverTargetChartOptions = {}
): IOverTargetChart {
  if (!result) return { stations: [], labels: [], series: [], untargeted: [], isEmpty: true };

  const lines = result.lines;
  const allStations = result.stations.slice().sort((a, b) => a.order - b.order);

  // Index into each station's lines array by line key, so a station missing a line (it never
  // should, but the chart must not throw on it) reads as zero rather than undefined.
  const cellFor = (station: IStationOverTarget, line: IOverTargetLine) =>
    station.lines.find((l) => l.dismantlingBayId === line.dismantlingBayId);

  const stations = options.hideZeroStations
    ? allStations.filter((station) =>
        lines.some((line) => (cellFor(station, line)?.overCount ?? 0) > 0)
      )
    : allStations;

  const visible = (line: IOverTargetLine) =>
    !options.visibleKeys || options.visibleKeys.includes(lineKey(line));

  const named = lines.filter((line) => line.dismantlingBayId !== null);
  const coloured = named.slice(0, LINE_COLOURS.length);
  const overflow = named.slice(LINE_COLOURS.length);
  const noLine = lines.find((line) => line.dismantlingBayId === null);

  const seriesFor = (line: IOverTargetLine, index: number): IOverTargetSeries => ({
    key: lineKey(line),
    label: line.name,
    colour: lineColour(line, index),
    overCounts: stations.map((s) => cellFor(s, line)?.overCount ?? 0),
    vehicleCounts: stations.map((s) => cellFor(s, line)?.vehicleCount ?? 0),
    overSeconds: stations.map((s) => cellFor(s, line)?.overSeconds ?? 0),
  });

  const series: IOverTargetSeries[] = coloured
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => visible(line))
    .map(({ line, index }) => seriesFor(line, index));

  const visibleOverflow = overflow.filter(visible);
  if (visibleOverflow.length > 0) {
    const sumOverflow = (s: IStationOverTarget, field: "overCount" | "vehicleCount" | "overSeconds") =>
      visibleOverflow.reduce((sum, line) => sum + (cellFor(s, line)?.[field] ?? 0), 0);
    series.push({
      key: OTHER_LINES_KEY,
      label: OTHER_LINES_LABEL,
      colour: NEUTRAL_LINE_COLOUR,
      overCounts: stations.map((s) => sumOverflow(s, "overCount")),
      vehicleCounts: stations.map((s) => sumOverflow(s, "vehicleCount")),
      overSeconds: stations.map((s) => sumOverflow(s, "overSeconds")),
    });
  }

  if (noLine && visible(noLine)) series.push(seriesFor(noLine, -1));

  return {
    stations,
    labels: stations.map((s) => s.name),
    series,
    untargeted: allStations.filter((s) => s.targetSeconds === null).map((s) => s.name),
    isEmpty: series.every((s) => s.overCounts.every((c) => c === 0)),
  };
}
